import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getApiError } from "@/lib/api";
import { fmtLog } from "@/lib/tz";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, FlaskConical, ExternalLink, Check, ArrowLeft } from "lucide-react";

const CRIMSON = "var(--brand)";

/** One section's AI settings page: its model, chosen from the master registry in
 *  AI Settings, a test button and what the section uses AI for.
 *  `load()`, `save(model)` and `test(model)` call that section's own endpoints. */
export default function SectionAiSettings({ title, intro, privacy, load, save, test, footer, testid }) {
  const [cfg, setCfg] = useState(null);
  const [model, setModel] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    load().then((c) => { setCfg(c); setModel(c.model); })
      .catch((e) => toast.error(getApiError(e, `Couldn't load ${title} settings.`)));
  }, [load, title]);

  const onSave = async () => {
    setSaving(true);
    try { const c = await save(model); setCfg(c); setModel(c.model); toast.success(`${title} settings saved`); }
    catch (e) { toast.error(getApiError(e, "Save failed.")); }
    finally { setSaving(false); }
  };

  const onTest = async () => {
    setTesting(true); setResult(null);
    try { setResult(await test(model)); }
    catch (e) { setResult({ ok: false, error: getApiError(e, "Test failed.") }); }
    finally { setTesting(false); }
  };

  if (!cfg) return <div className="p-5 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  const known = cfg.models.some((m) => m.id === model);
  const dirty = model !== cfg.model;
  return (
    <div className="p-5 max-w-3xl" data-testid={testid}>
      <Link to="/settings/ai" className="text-[11px] text-slate-400 hover:text-slate-600 inline-flex items-center gap-1 mb-2">
        <ArrowLeft className="h-3 w-3" /> AI Settings
      </Link>
      <div className="flex items-center gap-2.5 mb-1">
        <Sparkles className="h-5 w-5" style={{ color: CRIMSON }} />
        <h1 className="text-xl font-semibold text-slate-800">{title}</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        {intro} It's set separately from the other AI features, so changing it here doesn't affect them. Models come from
        the registry in <Link to="/settings/ai" className="underline hover:text-slate-700">AI Settings</Link>.
      </p>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-sm font-semibold text-slate-700">AI model</h2>
          <Link to="/settings/ai" className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5">
            <ExternalLink className="h-3 w-3" /> Manage models in AI Settings
          </Link>
        </div>
        <select value={model} onChange={(e) => { setModel(e.target.value); setResult(null); }}
          className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white" data-testid="section-ai-model">
          {cfg.models.map((m) => (
            <option key={m.id} value={m.id}>{m.label}{m.id === cfg.default_model ? " (default)" : ""}</option>
          ))}
          {!known && <option value={model}>{model} (no longer in the registry)</option>}
        </select>
        {!known && (
          <p className="text-[11px] text-amber-700 mt-1">This model has been removed from AI Settings. Choose another one and save.</p>
        )}
        <p className="text-[11px] text-slate-400 mt-1">
          {privacy} Use a model that keeps processing in Australia (its id starts with <code>au.</code>).
          {cfg.updated_at && <> Last changed {fmtLog(cfg.updated_at)}{cfg.updated_by ? ` by ${cfg.updated_by}` : ""}.</>}
        </p>

        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={onTest} disabled={testing || !model} data-testid="section-ai-test">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FlaskConical className="h-3.5 w-3.5 mr-1" />}Test model
          </Button>
          <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={onSave} disabled={saving || !dirty || !known} data-testid="section-ai-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}Save
          </Button>
          {dirty && <span className="text-[11px] text-amber-700">Not saved yet</span>}
        </div>
        {result && (
          <div className={`mt-3 rounded-md px-3 py-2 text-xs ${result.ok ? "bg-slate-50 text-slate-700" : "bg-rose-50 text-rose-700"}`} data-testid="section-ai-test-result">
            {result.ok ? (
              <><b>Reply:</b> {result.response}{result.latency_ms ? <span className="text-slate-400"> · {(result.latency_ms / 1000).toFixed(1)} s</span> : null}</>
            ) : result.error}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-2">Used for</h2>
        <ul className="space-y-2">
          {cfg.features.map((f) => (
            <li key={f.key} className="text-xs">
              <span className="font-medium text-slate-700">{f.label}</span>
              <span className="text-slate-500"> · {f.description}</span>
            </li>
          ))}
        </ul>
        {footer && <p className="text-[11px] text-slate-400 mt-3">{footer}</p>}
      </div>
    </div>
  );
}
